import React from 'react'

import {PositioningPortalWithState} from '@codastic/react-positioning-portal'
import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'
import pull from 'lodash/pull'

import useKeyPress from '../../hooks/use-key-press'
import cn from '../../libs/class-names'

import arrowDropDownIcon from '../../assets/icons/arrow-drop-down.svg'
import chevronDownIcon from '../../assets/icons/chevron-down.svg'

import Badge, {Props as BadgeProps} from '../badge/Badge'
import Icon from '../icon'
import SelectMenu from '../select-menu/select-menu'

export type Value<ValueType, AdditionalData extends Record<string, unknown>> = {
  value: ValueType
  label?: string // When not provided value will be shown
  // Special case because we have to be able to render the badge as a selected value
  badge?: {
    label: BadgeProps['label']
    type: BadgeProps['type']
  }
} & AdditionalData

export interface BaseProps<
  ValueType = unknown,
  AdditionalData extends Record<string, unknown> = Record<string, unknown>,
> {
  classNames?: string[]
  placeholder?: string
  icon?: React.ReactElement
  value?: Value<ValueType, AdditionalData> | Value<ValueType, AdditionalData>[]
  onChange?:
    | ((value: Value<ValueType, AdditionalData>, name: string | undefined) => void)
    | ((value: Value<ValueType, AdditionalData>[], name: string | undefined) => void)
  onBlur?: React.FocusEventHandler
  menu?: React.ReactElement // When not provided select field will be in constant mode
  mixed?: boolean
  mixedLabel?: string
  disabled?: boolean
  name?: string
  tiny?: boolean
  error?: boolean
  warning?: boolean
  inline?: boolean
  inlinePill?: boolean
  invert?: boolean
  strong?: boolean
}

const getLabel = (label?: string, value?: unknown) => label || `${value}`

export const BaseSelectField = <
  ValueType,
  AdditionalData extends Record<string, unknown> = Record<string, unknown>,
>({
  classNames,
  menu,
  name,
  placeholder = '',
  icon,
  mixed = false,
  mixedLabel,
  disabled = false,
  tiny = false,
  error = false,
  warning = false,
  inline = false,
  inlinePill = false,
  invert = false,
  strong = false,
  onChange = noop,
  onBlur = noop,
  value,
}: BaseProps<ValueType, AdditionalData>) => {
  const menuRef = React.useRef<SelectMenu>()

  const hasMultipleValues = (
    v: BaseProps<ValueType, AdditionalData>['value']
  ): v is Value<ValueType, AdditionalData>[] => Array.isArray(v)
  const hasMultipleValuesOnChange = (
    v: BaseProps<ValueType, AdditionalData>['value'],
    changeFn: BaseProps<ValueType, AdditionalData>['onChange']
  ): changeFn is (value: Value<ValueType, AdditionalData>[], name: string | undefined) => void =>
    Array.isArray(v)

  useKeyPress(event => {
    if (menuRef.current && menuRef.current.handleKeyDown) {
      menuRef.current.handleKeyDown(event.key)
    }
  })

  // TODO Badge? What is this here?
  const renderButton = ({isOpen = false, open = noop, close = noop} = {}) => (
    <button
      type="button"
      name={name}
      className={cn(
        'SelectField',
        {
          error,
          warning,
          disabled,
          selected: hasMultipleValues(value) ? !isEmpty(value) : !!value,
          mixed,
          constant: !menu,
          open: isOpen,
          inline,
          inlinePill,
          invert,
          tiny,
          strong,
        },
        classNames
      )}
      onBlur={event => {
        event.target.value = `${value}`
        event.target.name = `${name}`
        onBlur(event)
      }}
      onClick={isOpen ? close : open}
      disabled={!menu || disabled}
    >
      <span className="SelectField__value">
        {icon && <span className="SelectField__icon">{icon}</span>}
        {mixed && mixedLabel}
        {hasMultipleValues(value) ? (
          !isEmpty(value) ? (
            value.map((val, index) => (
              <span className="SelectField__label" key={index}>
                {getLabel(val.label, val.value)}
                {val.badge && <Badge size="s" label={val.badge.label} type={val.badge.type} />}
                {index + 1 < value.length && ', '}
              </span>
            ))
          ) : (
            placeholder
          )
        ) : (
          <>
            <span className="SelectField__label">
              {value ? getLabel(value.label, value.value) : mixed && mixedLabel ? '' : placeholder}
            </span>
            {value && value.badge && (
              <Badge size="s" label={value.badge.label} type={value.badge.type} />
            )}
          </>
        )}
      </span>
      <Icon source={inline ? arrowDropDownIcon : chevronDownIcon} />
    </button>
  )

  if (menu) {
    return (
      <PositioningPortalWithState
        portalContent={({relatedWidth, close}) => (
          <div
            className="SelectField__menu"
            style={{
              minWidth: relatedWidth,
            }}
          >
            {React.cloneElement(menu, {
              ref: menuRef,
              onClick: (v: Value<ValueType, AdditionalData>) => {
                if (hasMultipleValues(value) && hasMultipleValuesOnChange(value, onChange)) {
                  if (value.includes(v)) {
                    onChange(pull([...value], v), name)
                  } else {
                    onChange([...value, v], name)
                  }
                }

                if (!hasMultipleValues(value) && !hasMultipleValuesOnChange(value, onChange)) {
                  onChange(v, name)
                  close()
                }
              },
              selectedValue: value,
            })}
          </div>
        )}
      >
        {renderButton}
      </PositioningPortalWithState>
    )
  }

  return renderButton()
}

export interface Props<
  ValueType = unknown,
  AdditionalData extends Record<string, unknown> = Record<string, unknown>,
> extends Omit<BaseProps<ValueType, AdditionalData>, 'value' | 'onChange'> {
  value?: Value<ValueType, AdditionalData>
  onChange?: (value: Value<ValueType, AdditionalData>, name: string | undefined) => void
}

function SelectField<
  ValueType = unknown,
  AdditionalData extends Record<string, unknown> = Record<string, unknown>,
>(props: Props<ValueType, AdditionalData>) {
  return <BaseSelectField<ValueType, AdditionalData> {...props} />
}

export default SelectField
